import { candidateDetailActions } from '@AMIEWEB/Candidate/CandidateProfile/Profile/CandidateDetails.redux';
import { selectSkillSetTree } from '@AMIEWEB/Candidate/CandidateProfile/Profile/CandidateDetails.selector';
import { CustomBackdrop } from '@AMIEWEB/Common';
import { GenericTooltip } from '@AMIEWEB/Common/genericTooltip/GenericTooltip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useDecision } from '@optimizely/react-sdk';
import { List, ListItemButton, ListItemText, Popover } from '@mui/material';
import { ff_margin_tool_placement_profile } from 'app/constants/FeatureFlags/MarginTool/Keys';
import { selectUser } from 'oidc/user.selectors';
import { Authorized } from 'oidc/userHelper';
import { userRoles } from 'oidc/userRoles';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { selectPlacementMarginOrderDetails } from 'store/redux-store/placement-summary/selectors';
import { makeStyles } from 'tss-react/mui';
import {
  isCancellationStatus,
  isFC,
  isGoodToGo,
  isInterviewed,
  isInterviewScheduled,
  isOfferBookedByTraveler,
  isOfferByFacility,
  isOfferDeclinedByFacility,
  isOfferDeclinedByTraveler,
  isOkToSend,
  isPostponementWarning,
  isRFO,
  isSent,
  isTA,
  isWaitingGoodToGo,
} from '../NextPlacementStatus/StatusDefaults';
import { PlacementChangeDialog } from '../PlacementChange/PlacementChangeDialog';
import { ChangeOrderIDSearchDialog } from './ChangeOrderIDSearchDialog';
import { PlacementChangeModalSourceIndentifier } from 'app/constants';

export const useStyles = makeStyles<{ hasPermission: boolean }>()((theme, { hasPermission }) => ({
  upArrow: {
    color: theme.palette.framework.system.doveGray,
    cursor: 'pointer',
    opacity: '0.7',
  },
  downArrow: {
    color: hasPermission ? theme.palette.framework.system.navyBlue : theme.palette.framework.system.doveGray,
    cursor: 'pointer',
    opacity: '0.7',
  },
}));

export const ChangeOrderIDLink = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openMenuList, setOpenMenuList] = useState(false);
  const [anchorE1, setAnchorE1] = useState<HTMLElement | null>(null);
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [openChangePlacementDialog, setOpenChangePlacementDialog] = useState(false);
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const placementDetails = useSelector(selectPlacementDetails);
  const candidateSkillSets = useSelector(selectSkillSetTree)?.candidateSkillSets;
  const placementMarginOrderDetails = useSelector(selectPlacementMarginOrderDetails);
  const [featureFlagMarginTool] = useDecision(ff_margin_tool_placement_profile);
  const user = useSelector(selectUser);

  const isLaborDisruptionPlacement = placementDetails?.header?.brandId === 14;

  const hasMarginToolApprovedScenario =
    (featureFlagMarginTool?.enabled && placementMarginOrderDetails?.hasApprovedScenario) || false;

  const isPlacementStatusAllowed = useMemo(() => {
    return isLaborDisruptionPlacement
      ? isTA(currentPlacementStatus) ||
          isWaitingGoodToGo(currentPlacementStatus) ||
          isPostponementWarning(currentPlacementStatus) ||
          isGoodToGo(currentPlacementStatus)||
          isCancellationStatus(currentPlacementStatus) ||
          isOfferDeclinedByFacility(currentPlacementStatus) ||
          isOfferDeclinedByTraveler(currentPlacementStatus)
      : isRFO(currentPlacementStatus) ||
          isSent(currentPlacementStatus) ||
          isInterviewed(currentPlacementStatus) ||
          isOkToSend(currentPlacementStatus) ||
          isOfferByFacility(currentPlacementStatus) ||
          isOfferBookedByTraveler(currentPlacementStatus) ||
          isInterviewScheduled(currentPlacementStatus) ||
          isTA(currentPlacementStatus) ||
          isFC(currentPlacementStatus);
  }, [isLaborDisruptionPlacement, currentPlacementStatus]);

  let hasPermissionToLink = false;
  let errorMessageForTooltip = '';

  if (isLaborDisruptionPlacement) {
    const isAuthorized = Authorized(
      [
        userRoles.recruitment_TeamMember, 
        userRoles.recruitment_Leadership,
        userRoles.candidate_Contracts_Leadership,
        userRoles.candidate_Contracts_TeamMember,
      ],
      user.userInfo,
    );

    hasPermissionToLink = isPlacementStatusAllowed && isAuthorized && !hasMarginToolApprovedScenario;

    if (!hasPermissionToLink) {
      if (hasMarginToolApprovedScenario) {
        errorMessageForTooltip = t('placement.placementChange.marginScenariosDisableText');
      } else if (!isPlacementStatusAllowed) {
        errorMessageForTooltip = t('placement.placementChange.disableText');
      } else if (!isAuthorized) {
        errorMessageForTooltip = t('placement.placementChange.laborDisruptionDisableText');
      }
    }
  } else {
    hasPermissionToLink = isPlacementStatusAllowed && !hasMarginToolApprovedScenario;
    if (!hasPermissionToLink) {
      errorMessageForTooltip = hasMarginToolApprovedScenario
        ? t('placement.placementChange.marginScenariosDisableText')
        : t('placement.placementChange.disableText');
    }
  }

  const { classes } = useStyles({
    hasPermission: hasPermissionToLink,
  });

  const openMenu = event => {
    if (anchorE1 === null) {
      setAnchorE1(event.currentTarget);
      setOpenMenuList(true);
    }
  };

  const closeMenu = () => {
    setAnchorE1(null);
    setOpenMenuList(false);
  };

  useEffect(() => {
    dispatch(candidateDetailActions.getCandidateSkillSetTree({ candidateId: placementDetails?.header?.candidate?.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {hasPermissionToLink ? (
        <>
          <div onClick={event => (hasPermissionToLink ? openMenu(event) : event.preventDefault())}>
            {openMenuList ? (
              <KeyboardArrowUpIcon className={classes.upArrow} />
            ) : (
              <KeyboardArrowDownIcon className={classes.downArrow} />
            )}
          </div>
          <Popover
            open={openMenuList}
            anchorEl={anchorE1}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={() => closeMenu()}
          >
            <List>
              <ListItemButton
                id="changeOrderId"
                onClick={() => {
                  closeMenu();
                  isLaborDisruptionPlacement && hasPermissionToLink
                    ? setOpenChangePlacementDialog(true)
                    : setOpenSearchDialog(true);
                }}
              >
                <ListItemText primary={t('placement.OrderChangeID.menuTitle')} />
              </ListItemButton>
            </List>
          </Popover>
        </>
      ) : (
        <GenericTooltip title={errorMessageForTooltip}>
          <KeyboardArrowDownIcon className={classes.downArrow} />
        </GenericTooltip>
      )}
      {openSearchDialog && candidateSkillSets ? (
        <ChangeOrderIDSearchDialog openSearchDialog={openSearchDialog} setOpenSearchDialog={setOpenSearchDialog} />
      ) : (
        <>{openSearchDialog && <CustomBackdrop open={!candidateSkillSets} />}</>
      )}
      {openChangePlacementDialog && (
        <PlacementChangeDialog
          openDialog={openChangePlacementDialog}
          closeDialog={() => setOpenChangePlacementDialog(false)}
          source={PlacementChangeModalSourceIndentifier}
        />
      )}
    </>
  );
};
